// client/src/hooks/useNotificationsList.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ApiNotifications from '../api/ApiNotifications'
import { queryKeys } from '../queryClient'

/**
 * Custom hook to manage notifications list
 * Uses React Query for automatic caching and optimistic updates
 *
 * @returns {Object} - { notifications, loading, error, markAsRead, markAllAsRead, refresh }
 */
function useNotificationsList() {
  const queryClient = useQueryClient()

  // Fetch notifications list
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.notificationsList(),
    queryFn: async () => {
      const response = await ApiNotifications.getList()
      if (response?.success !== true) {
        const errorMessage =
          response?.error?.message ||
          response?.error ||
          'Failed to fetch notifications'
        throw new Error(errorMessage)
      }

      return Array.isArray(response?.data?.items) ? response.data.items : []
    },
    staleTime: 30000, // Consider data stale after 30 seconds
  })

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => ApiNotifications.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate both notifications list and count to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationsList() })
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationCount() })
    },
  })

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => ApiNotifications.markAllAsRead(),
    onSuccess: () => {
      // Invalidate both notifications list and count to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationsList() })
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationCount() })
    },
  })

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId) =>
      ApiNotifications.deleteNotification(notificationId),
    onSuccess: () => {
      // Invalidate both notifications list and count to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationsList() })
      queryClient.invalidateQueries({ queryKey: queryKeys.notificationCount() })
    },
  })

  return {
    notifications,
    loading: isLoading,
    error,
    markAsRead: (notificationId) => markAsReadMutation.mutate(notificationId),
    markAllAsRead: () => markAllAsReadMutation.mutate(),
    deleteNotification: (notificationId) =>
      deleteNotificationMutation.mutate(notificationId),
    refresh: refetch,
  }
}

export default useNotificationsList
